import { useEffect, useMemo, useState } from "react";
import { usePageSEO } from "../hooks/usePageSEO";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft, ExternalLink, Calendar, Bell, Check, Sparkles,
  Brain, ShieldCheck, MessageSquare, ArrowRight, Cpu,
} from "lucide-react";
import { LOGO_URL } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDateLong = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
};
const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function TechWebinarLanding() {
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/webinars/next`, { params: { kind: "technical" } });
        setWebinar(r.data?.ok && r.data?.webinar ? r.data.webinar : null);
      } catch {
        setWebinar(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dateLong = fmtDateLong(webinar?.scheduled_at);
  const timeLabel = fmtTime(webinar?.scheduled_at);
  const daysUntil = useMemo(() => {
    if (!webinar?.scheduled_at) return null;
    const diff = new Date(webinar.scheduled_at).getTime() - Date.now();
    return diff > 0 ? Math.ceil(diff / 86400000) : null;
  }, [webinar]);

  // WhatsApp reminder (same contract as /webinartech/danke so mail deep
  // links ?open_reminder=1#reminder keep working on this page too).
  const [waName, setWaName] = useState("");
  const [waCC, setWaCC] = useState("+41");
  const [waPhone, setWaPhone] = useState("");
  const [waSubmitting, setWaSubmitting] = useState(false);
  const [waError, setWaError] = useState("");
  const [waDone, setWaDone] = useState(false);

  usePageSEO({
    description: "Das Alphalane Technik-Webinar: Ein Blick unter die Haube des KI-Trading-Systems — Architektur, Risikomanagement und Live-Demo. Jetzt kostenlos anmelden.",
    canonical: "https://alphalane-tradingsystems.com/webinartech",
  });

  useEffect(() => {
    document.title = "Alphalane Technik-Webinar — Ein Blick unter die Haube";
    try {
      const params = new URLSearchParams(window.location.search);
      const nameParam = params.get("name");
      if (nameParam) setWaName(nameParam.slice(0, 60));
      if (params.get("open_reminder") === "1" || window.location.hash === "#reminder") {
        setTimeout(() => {
          const el = document.querySelector('[data-testid="webinar-whatsapp-reminder"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          const phone = document.querySelector('[data-testid="webinar-reminder-phone"]');
          if (phone) phone.focus({ preventScroll: true });
        }, 350);
      }
    } catch { /* noop */ }
  }, []);

  const submitReminder = async (e) => {
    e?.preventDefault?.();
    setWaError("");
    if (!waName.trim()) { setWaError("Bitte gib deinen Vornamen ein."); return; }
    const digits = (waPhone || "").replace(/\D+/g, "");
    if (digits.length < 6) { setWaError("Bitte gib deine WhatsApp-Nummer ein (ohne Ländercode)."); return; }
    setWaSubmitting(true);
    try {
      await axios.post(`${API}/webinar/reminder`, {
        name: waName.trim(),
        phone: `${waCC}${digits}`,
        webinar_id: webinar?.id || undefined,
      });
      setWaDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Da ist etwas schiefgegangen. Bitte versuch es nochmal.";
      setWaError(typeof msg === "string" ? msg : "Fehler beim Speichern.");
    } finally {
      setWaSubmitting(false);
    }
  };

  return (
    <div data-testid="techwebinar-landing" className="min-h-screen bg-[var(--al-bg)]">
      <header className="border-b border-[var(--al-line)] bg-white/70 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-auto" />
            <span className="hidden sm:inline font-display font-bold text-base text-slate-900">Alphalane</span>
          </Link>
          <Link to="/" data-testid="techwebinar-back-home" className="text-xs sm:text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ChevronLeft className="h-4 w-4" /> Zur Startseite
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        {/* Hero */}
        <section className="text-center mb-14" data-testid="techwebinar-hero">
          <span className="al-tag justify-self-center">
            <Cpu className="h-3 w-3" /> Technik-Webinar · mit Adrian Bienz
          </span>
          <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Ein Blick <span className="al-grad-text">unter die Haube.</span>
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Wie arbeitet das Alphalane-System technisch? Unser Trader &amp; Tech-Profi
            <strong className="text-slate-900"> Adrian Bienz</strong> zeigt es dir live — Architektur, Ausführung,
            Broker-Anbindung und das Setup deines Kontos. Inklusive offener Technik-Q&amp;A.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="#anmeldung"
              data-testid="techwebinar-hero-cta"
              className="al-cta text-base font-display font-bold !px-8 !py-4"
            >
              Jetzt Platz sichern <ArrowRight className="h-5 w-5" />
            </a>
            {daysUntil != null && (
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-sm font-display font-semibold">
                <Calendar className="h-4 w-4" /> Nächster Termin in {daysUntil} {daysUntil === 1 ? "Tag" : "Tagen"}
              </span>
            )}
          </div>
        </section>

        {/* Nächster Termin + Anmeldung */}
        <section id="anmeldung" className="mb-14 al-card overflow-hidden scroll-mt-24" data-testid="techwebinar-register">
          <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 p-8 sm:p-12 text-center text-white rounded-[18px]">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] uppercase tracking-widest font-bold mb-3 text-blue-200">
              <Calendar className="h-3 w-3" /> Nächster Technik-Call
            </div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-1" data-testid="techwebinar-date">
              {loading ? "Termin wird geladen…" : (dateLong || "Aktuell ist kein Termin geplant")}
            </h2>
            <p className="font-display text-blue-200 text-lg mb-2">
              {timeLabel ? `${timeLabel} Uhr · Schweizer Zeit · Online (Zoho Webinar)` : (loading ? "" : "Der nächste Termin wird hier angekündigt — schau bald wieder vorbei.")}
            </p>
            {webinar?.title && <p className="text-sm text-blue-300 mb-6">{webinar.title}</p>}
            {webinar?.zoho_link ? (
              <a
                href={webinar.zoho_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="techwebinar-zoho-register"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-slate-900 font-display font-bold text-base shadow-xl hover:scale-[1.02] transition-transform"
              >
                <ExternalLink className="h-5 w-5" /> Jetzt kostenlos anmelden
              </a>
            ) : (
              !loading && (
                <span className="inline-block text-sm text-blue-300 italic">
                  Anmelde-Link folgt, sobald der Termin steht.
                </span>
              )
            )}
            <p className="mt-4 text-xs text-blue-300">
              Die Anmeldung läuft über Zoho Webinar — dauert 30 Sekunden, den Zugangs-Link bekommst du sofort per Mail.
            </p>
          </div>
        </section>

        {/* Was dich erwartet */}
        <section className="mb-14 al-card p-7 sm:p-10 bg-gradient-to-br from-white to-blue-50/40" data-testid="techwebinar-expect">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-blue-600" />
            <span className="al-tag">Was dich im Technik-Call erwartet</span>
          </div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight tracking-tight mb-5">
            Technik, Setup &amp; <span className="al-grad-text">alle Detailfragen.</span>
          </h2>
          <div className="space-y-4 text-[15px] sm:text-base text-slate-700 leading-relaxed">
            <p>
              Der Technik-Call ist die Ergänzung zu unserem Live-Webinar: kein Verkauf, keine Strategie-Show —
              sondern <strong className="text-slate-900">der technische Deep-Dive</strong> für alle, die genau
              verstehen wollen, wie das System arbeitet und wie das eigene Konto aufgesetzt wird.
            </p>
            <p>
              Am Ende beantwortet Adrian in einer <strong className="text-slate-900">offenen Q&amp;A-Runde</strong> jede
              technische Frage — vom Broker-Setup bis zur Order-Ausführung.
            </p>
          </div>
          <div className="mt-7 grid sm:grid-cols-3 gap-3">
            {[
              { icon: Brain, text: "System-Architektur & Ausführung — live erklärt" },
              { icon: ShieldCheck, text: "Konto-Setup & Broker-Anbindung Schritt für Schritt" },
              { icon: MessageSquare, text: "Technik-Q&A mit Adrian — frag alles" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-start gap-3 p-4 rounded-xl border border-[var(--al-line)] bg-white">
                <div className="h-9 w-9 rounded-lg al-grad-bg flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4.5 w-4.5 text-white" style={{ height: 18, width: 18 }} />
                </div>
                <span className="text-sm text-slate-700 leading-snug">{text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Host */}
        <section className="mb-14 al-card p-7 sm:p-10" data-testid="techwebinar-host">
          <div className="flex items-center gap-5">
            <img
              src="/adrian-avatar.png"
              alt="Adrian Bienz"
              className="h-20 w-20 rounded-full border-2 border-blue-200 object-cover flex-shrink-0"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-blue-700">Dein Host</div>
              <div className="font-display font-bold text-xl mt-0.5">Adrian Bienz</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Trader &amp; Tech-Profi bei Alphalane Trading Systems. Adrian leitet die Technik-Calls
                und beantwortet die Q&amp;A — direkt, ehrlich und ohne Fachchinesisch.
              </p>
            </div>
          </div>
        </section>

        {/* WhatsApp Reminder */}
        <section id="reminder" className="mb-14 al-card overflow-hidden scroll-mt-24" data-testid="webinar-whatsapp-reminder">
          <div className="p-6 sm:p-8 bg-gradient-to-br from-emerald-50/80 via-white to-blue-50/40">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md shadow-emerald-200">
                <Bell className="h-6 w-6 text-white" strokeWidth={2.2} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-display font-bold text-xl sm:text-2xl leading-tight tracking-tight">
                  Erinnere mich <span className="al-grad-text">1 Stunde vorher</span> per WhatsApp.
                </h2>
                <p className="mt-1.5 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
                  Du verpasst den Call garantiert nicht — eine kurze WhatsApp 60 Min. vor Start.
                </p>
                {waDone ? (
                  <div data-testid="webinar-reminder-success" className="mt-5 inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-display font-semibold">
                    <Check className="h-5 w-5" strokeWidth={3} /> Reminder aktiviert. Wir schicken dir 1 h vorher eine WhatsApp.
                  </div>
                ) : (
                  <form onSubmit={submitReminder} className="mt-5 grid sm:grid-cols-12 gap-2.5" data-testid="webinar-reminder-form">
                    <input
                      type="text"
                      value={waName}
                      onChange={(e) => setWaName(e.target.value)}
                      placeholder="Vorname"
                      data-testid="webinar-reminder-name"
                      className="al-input sm:col-span-3"
                    />
                    <input
                      type="text"
                      value={waCC}
                      onChange={(e) => setWaCC(e.target.value)}
                      data-testid="webinar-reminder-cc"
                      className="al-input sm:col-span-2"
                    />
                    <input
                      type="tel"
                      value={waPhone}
                      onChange={(e) => setWaPhone(e.target.value)}
                      placeholder="79 123 45 67"
                      data-testid="webinar-reminder-phone"
                      className="al-input sm:col-span-4"
                    />
                    <button
                      type="submit"
                      disabled={waSubmitting}
                      data-testid="webinar-reminder-submit"
                      className="sm:col-span-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-display font-semibold disabled:opacity-50 transition-colors"
                    >
                      <Bell className="h-4 w-4" /> Reminder aktivieren
                    </button>
                    {waError && (
                      <div data-testid="webinar-reminder-error" className="sm:col-span-12 text-xs text-rose-600">{waError}</div>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        <p className="text-center text-xs text-slate-400">
          Bereits angemeldet? Nach der Zoho-Registrierung findest du alle Infos auf der{" "}
          <Link to="/webinartech/danke" data-testid="techwebinar-danke-link" className="underline hover:text-slate-600">Bestätigungsseite</Link>.
        </p>
      </main>
    </div>
  );
}
